import { Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { KpiChip } from '../../kpi/components/KpiChip.js';

/**
 * Main-metric hero card — 4th tile of the Daily Dashboard top row.
 * Mirrors the legacy `sec_070` layout: the license's `mainMetricId`
 * resolves to a metric whose `Quantity` + `Amount` totals (summed
 * across the filtered scenario set + active date range) render as
 * two big sub-numbers, with the metric's icon chip on the left.
 *
 * Empty state (no main metric configured) renders a small pointer to
 * KPI Builder. `useEffectiveMainMetricId()` synthesizes a fallback in
 * demo mode so this state is rare in practice.
 */
export interface MainMetricCardProps {
  metricName: string | null;
  metricIcon: string | null;
  metricColor: string | null;
  quantity: number;
  amount: number;
}

export function MainMetricCard({
  metricName,
  metricIcon,
  metricColor,
  quantity,
  amount,
}: MainMetricCardProps): JSX.Element {
  if (!metricName) {
    return (
      <article className="flex flex-col gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 p-4">
        <header className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-800 dark:text-amber-200">
          <Target className="h-3.5 w-3.5" /> Main metric
        </header>
        <p className="text-sm text-foreground/80">
          Pick a metric to anchor the rollup —{' '}
          <Link to="/kpi" className="underline underline-offset-2 hover:text-foreground">
            KPI Builder
          </Link>
          .
        </p>
      </article>
    );
  }
  return (
    <article className="rounded-lg border border-foreground/10 bg-foreground/[0.03] p-4">
      <header className="mb-3 flex items-center gap-2 text-sm text-foreground/65">
        <KpiChip icon={metricIcon ?? 'bar-chart'} color={metricColor ?? '#94a3b8'} size="sm" />
        <span className="truncate font-medium text-foreground/85" title={metricName}>
          {metricName}
        </span>
      </header>
      <div className="flex items-end gap-6">
        <div>
          <div className="text-2xl font-semibold tabular-nums text-foreground">
            {quantity.toLocaleString()}
          </div>
          <div className="text-xs text-foreground/50">Quantity</div>
        </div>
        <div>
          <div className="text-2xl font-semibold tabular-nums text-foreground">
            ${amount.toLocaleString()}
          </div>
          <div className="text-xs text-foreground/50">Amount</div>
        </div>
      </div>
    </article>
  );
}
